import { createSlice } from "@reduxjs/toolkit";
import { Post, Meta } from "src/store/types";
import {
  doFetchPostList,
  doFetchReportedPosts,
  doBlockUnblockPost,
  doDeletePost,
  doFetchPostReports,
  doPinUnpinPost,
} from "./post.action";

interface PostState {
  posts: Post[];
  reportedPosts: Post[];
  meta: Meta | null;
  reportedMeta: Meta | null;
  loading: boolean;
  reportedLoading: boolean;
  error: string | null;
  searchQuery: string;
  reportedSearchQuery: string;
  reportDetails: [];
  reportDetailsLoading: boolean;
}

const initialState: PostState = {
  posts: [] as Post[],
  reportedPosts: [] as Post[],
  meta: null,
  reportedMeta: null,
  loading: false,
  reportedLoading: false,
  error: null,
  searchQuery: "",
  reportedSearchQuery: "",
  reportDetails: [],
  reportDetailsLoading: false,
};

export const postSlice = createSlice({
  name: "post",
  initialState,
  reducers: {
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    clearSearch: (state) => {
      state.searchQuery = "";
    },
    setReportedSearchQuery: (state, action) => {
      state.reportedSearchQuery = action.payload;
    },
    clearReportedSearch: (state) => {
      state.reportedSearchQuery = "";
    },
  },
  extraReducers: (builder) => {
    // fetch post list
    builder
      .addCase(doFetchPostList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(doFetchPostList.fulfilled, (state, action) => {
        state.loading = false;
        state.posts = action.payload.data;
        state.meta = action.payload.meta;
      })
      .addCase(doFetchPostList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // fetch reported posts
    builder
      .addCase(doFetchReportedPosts.pending, (state) => {
        state.reportedLoading = true;
        state.error = null;
      })
      .addCase(doFetchReportedPosts.fulfilled, (state, action) => {
        state.reportedLoading = false;
        state.reportedPosts = action.payload.data;
        state.reportedMeta = action.payload.meta;
      })
      .addCase(doFetchReportedPosts.rejected, (state, action) => {
        state.reportedLoading = false;
        state.error = action.payload as string;
      });

    // fetch post reports
    builder
      .addCase(doFetchPostReports.pending, (state) => {
        state.reportDetailsLoading = true;
        state.error = null;
      })
      .addCase(doFetchPostReports.fulfilled, (state, action) => {
        state.reportDetailsLoading = false;
        state.reportDetails = action.payload.reports || [];
      })
      .addCase(doFetchPostReports.rejected, (state, action) => {
        state.reportDetailsLoading = false;
        state.error = action.payload as string;
      });

    // block/unblock post
    builder
      .addCase(doBlockUnblockPost.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(doBlockUnblockPost.fulfilled, (state, action) => {
        state.loading = false;
        // Update the post in both arrays if it exists
        if (action.payload.data) {
          const updatedPost = action.payload.data;
          state.posts = state.posts.map((post) =>
            post.pid === updatedPost.pid ? updatedPost : post
          );
          state.reportedPosts = state.reportedPosts.map((post) =>
            post.pid === updatedPost.pid ? updatedPost : post
          );
        }
      })
      .addCase(doBlockUnblockPost.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // pin/unpin post
    builder
      .addCase(doPinUnpinPost.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(doPinUnpinPost.fulfilled, (state, action) => {
        state.loading = false;
        // Update the post in both arrays if it exists
        if (action.payload.data) {
          const updatedPost = action.payload.data;
          state.posts = state.posts.map((post) =>
            post.pid === updatedPost.pid ? updatedPost : post
          );
          state.reportedPosts = state.reportedPosts.map((post) =>
            post.pid === updatedPost.pid ? updatedPost : post
          );
        }
      })
      .addCase(doPinUnpinPost.rejected,(state,action)=>{
        state.loading =false;
        state.error = action.payload as string;
      })

    // delete post
    builder
      .addCase(doDeletePost.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(doDeletePost.fulfilled, (state, action) => {
        state.loading = false;
        // Remove the post from both arrays
        const deletedPostId = action.payload.pid;
        state.posts = state.posts.filter((post) => post.pid !== deletedPostId);
        state.reportedPosts = state.reportedPosts.filter(
          (post) => post.pid !== deletedPostId
        );
      })
      .addCase(doDeletePost.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

// Export actions
export const {
  setSearchQuery,
  clearSearch,
  setReportedSearchQuery,
  clearReportedSearch,
} = postSlice.actions;

export default postSlice.reducer;
